import { Theme } from '@mui/material/styles'
import { SxProps } from '@mui/system'
import { Globals } from 'csstype'
import { ValueOf } from 'type-fest'

import { padPx } from '@/css-helpers/units'
import { css } from '@/styledComponents'

const comfortaa = 'Comfortaa, sans-serif'
const openSans = "'Open Sans', Roboto, Helvetica, sans-serif"
export const defaultFontFamily = openSans
const BASE_FONT_SIZE = 16

export const FontFamily = {
	Comfortaa: comfortaa,
	Default: openSans,
	/** This is the default */
	OpenSans: openSans,
} as const

export type FontFamily = ValueOf<typeof FontFamily>

/**
 * @deprecated
 * @property {string|FontType} family -  Use `FontType` enum (recommended) or supply your own font face name
 * @property {number} size - Font size will be transformed to `rem` unit based base font size. Available values are: 32, 20, 18, 16, 14, 12, 10, 8
 * @property {string|number} weight - Font weight. Any CSS valid value is valid
 */
export interface FontProps {
	/** Use `FontType` enum (recommended) or supply your own font face name */
	family: FontTypeOrFamily
	/** Font size will be transformed to `rem` unit based base font size. Also automatically sets line-height appropriately. */
	size: FontSize
	/** Font weight */
	weight: FontWeight
	/** Font style */
	style: Globals | 'italic' | 'normal' | 'oblique'
	letterSpacing: Globals | number | 'normal'
	textWrap: 'balance' | 'pretty' | 'stable' | 'wrap' | 'nowrap'
}

export type FontSize = 50 | 40 | 32 | 24 | 23 | 22 | 20 | 18 | 16 | 14 | 13 | 12 | 11 | 10 | 8
export type FontWeight =
	| 100
	| 200
	| 300
	| 400
	| 500
	| 600
	| 700
	| 800
	| 900
	| Globals
	| React.CSSProperties['fontWeight']
	| 'bold'
	| 'normal'
	| 'bolder'
	| 'lighter'

/**
 * @deprecated
 * @property {boolean} useDefaultBase - if false, only specified properties will be passed, instead of all font properties with default values
 * @property {boolean} useImportant - if true, appends `!important` to each property declaration
 */
export interface FontHelperSettings {
	/** Should the helper supply all font properties, or only the ones used in the override. defaults to `true` */
	useDefaultBase: boolean
	/** Should the helper add !important tags to properties. USE WITH CAUTION! */
	useImportant: boolean
}

/**
 * @deprecated Use muiUtils.typography or <Typography />
 * For use with the `font()` utility only
 */
// eslint-disable-next-line no-restricted-syntax
export enum FontType {
	OpenSans = 'Open Sans',
	Comfortaa = 'Comfortaa',
	RobotoMono = 'Roboto Mono',
}

type FontTypeOrFamily = FontType | string

/** @deprecated */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isFontProps(obj: any): obj is FontProps {
	return typeof obj === 'object' && Object.keys(obj).some((k) => ['family', 'size', 'weight'].includes(k))
}

/** @deprecated */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isHelperConf(obj: any): obj is FontHelperSettings {
	return typeof obj === 'object' && Object.keys(obj).some((k) => ['useDefaultBase', 'useImportant'].includes(k))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PropTransformer<T> = (v: T) => [string, any]
type LineHeightCalcFn = (size: FontProps['size'], family: FontProps['family']) => number

const lineHeightMap: Partial<Record<FontProps['size'], LineHeightCalcFn | number>> = {
	50: 60,
	40: 50,
	32: 38,
	24: 38,
	22: 28,
	20: 28,
	18: 25,
	16: 22,
	14: 20,
	12: 18,
	10: 14,
	8: 12,
}

/** @deprecated */
export function getLineHeight(fontSize: FontSize, fontFamily?: FontProps['family']): number | undefined {
	const _size = lineHeightMap[fontSize]
	if (typeof _size === 'function') {
		return _size(fontSize, fontFamilyToFont(fontFamily))
	} else {
		return _size
	}
}

/** @deprecated */
function fontFamilyToFont(family?: FontType | string): string {
	return family === FontType.OpenSans
		? defaultFontFamily
		: family === FontType.Comfortaa
			? comfortaa
			: (family ?? defaultFontFamily)
}

/**
 * @deprecated Use muiUtils.typography or <Typography
 * Returns CSS text for font properties.
 */
export function font(
	fontFamily?: FontTypeOrFamily,
	fontProps?: Partial<Omit<FontProps, 'family'>>,
	helperConf?: Partial<FontHelperSettings>,
): string
/**
 * @deprecated Use muiUtils.typography or <Typography
 * Returns CSS text for font properties.
 */
export function font(fontProps: Partial<FontProps>, helperConf?: Partial<FontHelperSettings>): string
/**
 * @deprecated Use muiUtils.typography or <Typography
 * Returns CSS text for font properties.
 */
export function font(
	a: FontTypeOrFamily | Partial<FontProps> = {},
	b: Partial<Omit<FontProps, 'family'>> | Partial<FontHelperSettings> = {},
	c: Partial<FontHelperSettings> = {},
): string {
	let fontFamily: FontTypeOrFamily
	let customProps: Partial<Omit<FontProps, 'family'>>
	let customConfig: Partial<FontHelperSettings>

	if (isFontProps(a)) {
		fontFamily = a.family
		const { family, ...rest } = a
		customProps = rest
		if (isHelperConf(b)) {
			customConfig = b
		}
	} else {
		fontFamily = a as FontTypeOrFamily
		customProps = b as FontProps
		customConfig = c
	}

	const defaultFontProps: FontProps = {
		family: fontFamilyToFont(fontFamily),
		size: 14,
		weight: 'normal',
		style: 'normal',
		letterSpacing: 'normal',
		textWrap: 'wrap',
	}
	const defaultConf: FontHelperSettings = {
		useDefaultBase: true,
		useImportant: false,
	}
	const allProps: FontProps = { ...defaultFontProps, ...customProps }
	const config: FontHelperSettings = { ...defaultConf, ...customConfig! }
	const { useDefaultBase } = config
	const important = config.useImportant ? ' !important' : ''
	const propNames: Array<keyof FontProps> = Object.keys(defaultFontProps) as Array<keyof FontProps>
	const propTransformers: object & Partial<Record<keyof FontProps, PropTransformer<FontProps[keyof FontProps]>>> = {
		size: (v) => ['font-size', typeof v === 'number' ? pixelFontSize(v) : v],
		letterSpacing: (v) => ['letter-spacing', padPx(v)],
		textWrap: (v) => ['text-wrap', v],
	}

	return propNames.reduce((str, p) => {
		const transformer = propTransformers.hasOwnProperty(p)
			? propTransformers[p]!
			: // eslint-disable-next-line @typescript-eslint/no-explicit-any
				(((v: any) => [`font-${p}`, v]) as PropTransformer<any>)
		const [name, value] = transformer(allProps[p])
		// @ts-expect-error todo if you see this please remove this comment and fix the type error
		if (!useDefaultBase && !customProps[p] && (p !== 'family' || allProps.family === defaultFontFamily)) {
			return str
		}
		let out = str + `\n${name}: ${value}${important};`
		if (p === 'size') {
			const lineHeightValue = getLineHeight(allProps.size, allProps.family)
			out += lineHeightValue ? `\nline-height: ${padPx(lineHeightValue)}${important};` : ''
		}
		return out
	}, '')
}

/** @deprecated Use theme.typography.pxToRem() */
export function pixelFontSize(pixels: number): string {
	return (pixels / BASE_FONT_SIZE).toFixed(3).replace(/\.?0+$/, '') + 'rem'
}

/** @deprecated Use "getTextOverflowSx" instead */
export const textOverflow = (maxWidth?: number | string) => css`
	${maxWidth ? `max-width: ${padPx(maxWidth)};` : ''}
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`

export const getTextOverflowSx = (maxWidth?: number | string): SxProps<Theme> => ({
	...(maxWidth ? { maxWidth } : {}),
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
})

/**
 * @deprecated Use MUI
 * Generate ellipsis for element after text overflows, that spans multiple lines.
 * **Important: Applies font props internally** */
export function textMultilineOverflow(fontProps: Partial<FontProps>, maxLinesToShow: number) {
	const lineHeight = lineHeightMap[fontProps.size!] as number
	const height = lineHeight * maxLinesToShow

	return `
		display: block; /* Fallback for non-webkit */
		display: -webkit-box;
		max-height: ${height}px; /* Fallback for non-webkit */
		${font(fontProps)}
		-webkit-line-clamp: ${maxLinesToShow};
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	`
}
